import type { StackedPoint, StackedSeries } from "../model/chart-data.js";

/**
 * TAXLANGAN ustunlar — bir kunning ichida turlar bo'yicha taqsimot.
 *
 * Nega taxlangan, yonma-yon emas: birinchi savol «o'sha kuni qancha
 * kirim bo'ldi», ikkinchisi «qaysi turdan». Yonma-yon ustunlar birinchi
 * savolni yo'q qiladi — jami ko'rinmaydi va odam uchta ustunni ko'zda
 * qo'shishga majbur bo'ladi.
 *
 * `TrendChart` bilan ayni qoida: kutubxonasiz sof SVG, ranglar CSS
 * o'zgaruvchilaridan — mavzu almashganda grafik o'zi moslashadi.
 */

/*
 * Geometriya `TrendChart` nikiga AYNAN teng va bu ataylab: ikki grafik
 * ekranda yonma-yon turadi va ikkovi ham o'sha davrning kirimini
 * ko'rsatadi. O'lchamlari farq qilsa, to'r chiziqlari va o'q yorliqlari
 * bir-biriga mos kelmay, ikki karta qiyshiq qo'yilgandek ko'rinadi.
 */
const W = 720;
const H = 260;
const PAD = { top: 16, right: 12, bottom: 26, left: 40 };

const INNER_W = W - PAD.left - PAD.right;
const INNER_H = H - PAD.top - PAD.bottom;

/** Ustunlar orasidagi tirqish, ustun kengligining ulushi. */
const GAP_RATIO = 0.3;

/** O'q bo'yicha "chiroyli" yuqori chegara (`TrendChart` dagi bilan bir xil). */
const niceMax = (value: number): number => Math.ceil(value / 50) * 50;

export interface StackedBarsProps {
  points: readonly StackedPoint[];
  series: readonly StackedSeries[];
  caption: string;
}

export const StackedBars = ({ points, series, caption }: StackedBarsProps) => {
  const totals = points.map((point) =>
    series.reduce((sum, item) => sum + (point.values[item.key] ?? 0), 0),
  );
  const max = niceMax(Math.max(1, ...totals));

  const slot = points.length === 0 ? INNER_W : INNER_W / points.length;
  /* Ustun eng kamida 2px: bir yillik davrda slot juda ingichka bo'lib
     qoladi va nol kenglikdagi to'rtburchak umuman chizilmaydi. */
  const barWidth = Math.max(2, slot * (1 - GAP_RATIO));

  /* To'rt oraliq — `TrendChart` dagi bilan bir xil zichlik. */
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
    y: PAD.top + INNER_H - ratio * INNER_H,
    value: Math.round(max * ratio),
  }));

  return (
    /*
      IZOH bu yerda YO'Q va bu ataylab: qatlamlarning nomi va rangi
      chaqiruvchidagi JAMI qatorida turadi va u yerda har turning aniq
      soni ham bor. Ikkovi chizilganda ekranda bir xil uchta nuqta va
      uchta yorliq ikki marta takrorlanardi — grafik ostida ham, uning
      tagida ham.
    */
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      role="img"
      aria-label={caption}
      preserveAspectRatio="xMidYMid meet"
    >
      {ticks.map((tick) => (
        <g key={tick.value}>
          <line
            x1={PAD.left}
            y1={tick.y}
            x2={W - PAD.right}
            y2={tick.y}
            stroke="var(--color-border)"
            strokeWidth={1}
          />
          <text
            x={PAD.left - 8}
            y={tick.y + 4}
            textAnchor="end"
            fontSize="11"
            fill="var(--color-text-faint)"
          >
            {tick.value}
          </text>
        </g>
      ))}

      {points.map((point, index) => {
        const x = PAD.left + index * slot + (slot - barWidth) / 2;
        let bottom = PAD.top + INNER_H;

        return (
          <g key={index}>
            {series.map((item) => {
              const value = point.values[item.key] ?? 0;
              if (value <= 0) return null;
              const height = (value / max) * INNER_H;
              bottom -= height;
              return (
                <rect
                  key={item.key}
                  x={x}
                  y={bottom}
                  width={barWidth}
                  height={height}
                  fill={item.color}
                />
              );
            })}
            {point.label === "" ? null : (
              <text
                x={x + barWidth / 2}
                y={H - 8}
                textAnchor="middle"
                fontSize="11"
                fill="var(--color-text-faint)"
              >
                {point.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};
